import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Box, Collapse, Grid, Typography, useMediaQuery, useTheme } from "@mui/material";
import Card from "../../common/Card";
import Table from "../../common/CustomTable";
import Button from "../../common/Button";
import {AssignmentOutlined, ExitToApp, KeyboardArrowDown, SearchOutlined, CloudDownload, DownloadOutlined} from '@mui/icons-material';
import { useForm } from 'react-hook-form';
import { useLocation, useNavigate } from 'react-router-dom';
import CustomTextField from '../../common/CustomTextField';
import { useAppDispatch } from '../../../redux/slice/redux-hooks';
import { ACTION_TYPE, CAN_ACCESS_APPLICATION, ENTITY_TYPE, IVR_STATUS, IVR_STATUS_LIST, ORDERTYPE, EXPORTABLE_ITEMS, AUDIT_ACTIONS } from '../../../constants/applicationConstants';
import CustomMultipleDropDown from '../../common/CustomMultipleDropDown';
import { CodeTable, fetchAllCodeTablesValues, getCodeTableValuesByTableName, useCodetablesSlice } from '../../../redux/slice/codetableSlice';
import CustomDateRange from '../../common/CustomDateRange';
import { DateObject } from 'react-multi-date-picker';
import CustomDropdownField from '../../common/CustomDropdownFiled';
import { auditLogInfo, fetchAuditLogList, resetAuditState, SearchAuditLog, useAuditLogSlice } from '../../../redux/slice/auditLog/auditLogSlice';
import { useNavigation } from '../../../hooks/useNavigation';
import { useFilterPanelFocus } from "../../../hooks/useFilterPanelFocus";
import CustomIconButton from '../../common/CustomIconButton';
import CustomJsonPopUp from '../../common/CustomJsonPopUp';
import { useSelector } from 'react-redux';
import { RootState } from '../../../redux/store';
import { selectIsValueFoundInToken } from '../../../redux/slice/authSlice';
import ExportToExcel from '../../common/ExportToExcel';
import { MenuItem, ListItemIcon } from "@mui/material";
import StyledMenu from "../../common/StyledMenu";
import { useModalStyles } from "../../common/useModalStyles";

interface Column {
  id: string;
  label: string;
  minWidth?: number;
  align?: "right" | "center" | "left";
  format?: (value: number) => string;
}

type Option = {
  value: string,
  label: string
};

const ITEMS_PER_PAGE = 20;

const AuditLogList: React.FC = () => {

  const location = useLocation();
  const params = new URLSearchParams(location.search);
  const { navigate } = useNavigation();
  const dispatch = useAppDispatch();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
  const isDark = theme.palette.mode === "dark";
  const { primaryBtnSx, tokens: modalTokens } = useModalStyles();
  const { auditLogList } = useAuditLogSlice();
  const entityName = params.get('entityName');
  const entityNumber = params.get('entityNumber');

  const { control, handleSubmit, reset, setValue, getValues } = useForm<SearchAuditLog>();
  const [showCard, setShowCard] = useState(false);
  const {
    filterPanelRef,
    toggleFilterPanel,
    handleFilterPanelEntering,
    handleFilterPanelEntered,
    handleFilterPanelExited,
  } = useFilterPanelFocus(showCard, setShowCard);

  const listBorderColor = isDark ? "rgba(148,163,184,0.2)" : "rgba(15,23,42,0.12)";
  const listPanelBg = isDark ? "#141720" : "rgba(255,255,255,0.82)";
  const listHoverBg = isDark ? "rgba(20,184,166,0.09)" : "rgba(20,184,166,0.06)";

  const listCardSx = {
    borderRadius: "14px",
    border: `1px solid ${listBorderColor}`,
    backgroundColor: listPanelBg,
    backgroundImage: "none",
    backdropFilter: "blur(8px)",
    boxShadow: isDark ? "0 12px 32px rgba(0,0,0,0.28)" : "0 12px 28px rgba(15,23,42,0.08)",
  };

  const filterPanelSx = {
    ...listCardSx,
    p: { xs: "14px !important", md: "18px !important" },
    position: "relative",
    zIndex: 20,
    overflow: "visible",
    "& .MuiAutocomplete-root, & .MuiFormControl-root": { width: "100%" },
    "& .MuiTextField-root": { height: "42px", minHeight: "42px !important" },
    "& .MuiOutlinedInput-root, & .MuiInputBase-root, & .MuiAutocomplete-inputRoot, & .MuiAutocomplete-root .MuiOutlinedInput-root": {
      minHeight: "42px !important",
      height: "42px !important",
      borderRadius: "10px !important",
      backgroundColor: `${modalTokens.inputBg} !important`,
      color: modalTokens.textPrimary,
      fontSize: "0.88rem",
      boxShadow: isDark ? "none" : "0 1px 3px rgba(0,0,0,0.06)",
      alignItems: "center",
      boxSizing: "border-box",
    },
    "& .MuiAutocomplete-root .MuiOutlinedInput-root": { padding: "0 38px 0 12px !important" },
    "& .MuiAutocomplete-root .MuiInputBase-input, & .MuiAutocomplete-input, & .MuiOutlinedInput-input": {
      paddingTop: "8px !important",
      paddingBottom: "8px !important",
      height: "24px !important",
      lineHeight: "24px !important",
      boxSizing: "border-box",
    },
    "& .MuiOutlinedInput-input": { paddingLeft: "12px !important", paddingRight: "12px !important" },
    "& .MuiAutocomplete-endAdornment": { top: "50% !important", transform: "translateY(-50%)", right: "8px !important" },
    "& .MuiOutlinedInput-notchedOutline, & .MuiOutlinedInput-root fieldset": { borderColor: `${modalTokens.inputBorder} !important` },
    "& .MuiOutlinedInput-root:hover fieldset, & .MuiOutlinedInput-root.Mui-focused fieldset": { borderColor: `${modalTokens.accent} !important`, borderWidth: "1px !important" },
    "& .MuiOutlinedInput-root.Mui-focused": { boxShadow: isDark ? "none" : "0 0 0 3px rgba(20,184,166,0.12)" },
    "& .MuiInputBase-input": { color: modalTokens.textPrimary, fontSize: "0.88rem" },
    "& input::selection": { color: modalTokens.textPrimary, backgroundColor: isDark ? "rgba(20,184,166,0.28)" : "rgba(20,184,166,0.2)" },
    "& .MuiOutlinedInput-root:has(input:-webkit-autofill)": { backgroundColor: "#376c9f !important", "& fieldset": { borderColor: "#7fb1df !important" } },
    "& input:-webkit-autofill, & input:-webkit-autofill:hover, & input:-webkit-autofill:focus": { WebkitTextFillColor: "#ffffff", caretColor: "#ffffff", WebkitBoxShadow: "0 0 0 100px #376c9f inset", borderRadius: "10px", transition: "background-color 5000s ease-in-out 0s" },
    "& .MuiInputLabel-root": { color: isDark ? "rgba(255,255,255,0.5)" : "rgba(0,0,0,0.45)", fontSize: "0.88rem" },
    "& .MuiInputLabel-root.Mui-focused": { color: `${modalTokens.accent} !important` },
    "& .MuiInputLabel-outlined:not(.MuiInputLabel-shrink)": { transform: "translate(14px, 9px) scale(1)" },
    "& .MuiChip-root": { borderRadius: "999px", backgroundColor: isDark ? "rgba(20,184,166,0.16)" : "rgba(20,184,166,0.1)", color: modalTokens.accent, border: `1px solid ${isDark ? "rgba(20,184,166,0.26)" : "rgba(20,184,166,0.18)"}`, fontWeight: 600 },
    "& .MuiSvgIcon-root": { color: modalTokens.textSecondary },
    "& .rmdp-container": { width: "100%", height: "42px", overflow: "visible", zIndex: 30 },
    "& .rmdp-wrapper, & .rmdp-calendar, & .rmdp-day-picker": { height: "auto !important", minHeight: "unset !important", overflow: "visible" },
    "& .rmdp-wrapper": { zIndex: "1600 !important", position: "absolute" },
  };

  const primaryActionBtnSx = {
    ...primaryBtnSx,
    color: "#fff",
    "& .MuiTypography-root": { color: "#fff", fontWeight: 700 },
    "& .MuiButton-startIcon": { color: "#fff" },
  };

  const outlineBtnSx = {
    textTransform: "none" as const,
    fontWeight: 700,
    fontSize: "0.86rem",
    borderRadius: "10px",
    py: 1.05,
    px: 1.6,
    color: "#00B8D9",
    borderColor: "rgba(0,184,217,0.55)",
    backgroundColor: isDark ? "rgba(0,184,217,0.08)" : "rgba(0,184,217,0.04)",
    whiteSpace: "nowrap" as const,
    "&:hover": { color: "#fff", borderColor: "#00B8D9", backgroundColor: "#00B8D9", boxShadow: "0 6px 22px rgba(0,184,217,0.28)" },
    "& .MuiTypography-root": { color: "inherit", fontWeight: 700 },
    "& .MuiButton-startIcon, & .MuiSvgIcon-root": { color: "inherit" },
  };

  const exportBtnSx = {
    textTransform: "none" as const,
    fontWeight: 700,
    fontSize: "0.86rem",
    borderRadius: "10px",
    py: 1.05,
    px: 1.6,
    color: "#14B8A6",
    borderColor: "rgba(20,184,166,0.55)",
    backgroundColor: isDark ? "rgba(20,184,166,0.08)" : "rgba(20,184,166,0.04)",
    whiteSpace: "nowrap" as const,
    "& .MuiTypography-root": { color: "inherit", fontWeight: 700 },
    "& .MuiSvgIcon-root": { color: "#14B8A6" },
    "&:hover": {
      color: "#fff",
      borderColor: "#14B8A6",
      backgroundColor: "#14B8A6",
      boxShadow: "0 6px 22px rgba(20,184,166,0.28)",
      "& .MuiSvgIcon-root": { color: "#fff" },
    },
  };

  const auditTableSx = {
    "& .MuiTableContainer-root": { borderRadius: "12px", border: "none", backgroundColor: "transparent" },
    "& table": {  borderCollapse: "collapse", borderSpacing: 0 },
    "& .MuiTableCell-root": { borderBottom: `1px solid ${isDark ? "rgba(148,163,184,0.14)" : "rgba(15,23,42,0.08)"}`, py: 1.15, fontSize: "0.84rem" },
    "& .MuiTableHead-root .MuiTableCell-root": { height: "42px"  },
    "& .MuiTableHead-root .MuiTypography-root": { color: "#00B8D9 !important", fontSize: "0.78rem", fontWeight: 800, letterSpacing: "0.02em" },
    "& .MuiTableBody-root .MuiTableRow-root": { transition: "background-color 0.18s ease, box-shadow 0.18s ease" },
    "& .MuiTableBody-root .MuiTableRow-root:hover": { backgroundColor: `${listHoverBg} !important` },
    "& .MuiTableBody-root .MuiTableCell-root": { color: modalTokens.textPrimary, fontSize: "0.84rem" },
    "& .MuiTableBody-root .MuiTypography-root": { fontSize: "0.84rem" },
    "& .MuiIconButton-root": { color: isDark ? "#94a3b8" : "#475569", borderRadius: "8px", "&:hover": { color: modalTokens.accent, backgroundColor: isDark ? "rgba(20,184,166,0.12)" : "rgba(20,184,166,0.08)" } },
    "& .MuiPaginationItem-root": { borderColor: listBorderColor, color: modalTokens.textSecondary, borderRadius: "8px", "&.Mui-selected": { color: "#fff", backgroundColor: modalTokens.accent, borderColor: modalTokens.accent } },
  };

  const [searchCriteria, setSearchCriteria] = useState<SearchAuditLog>({
    entityName: undefined,
    entityNumber: undefined,
    userName: undefined,
    actionType: undefined,
    fromDate: undefined,
    toDate: undefined,
    currentPage: 1,
    itemsPerPage: ITEMS_PER_PAGE
  });

  const { allCodeTablesValuesMap } = useCodetablesSlice();
  const [showAuditedObjectPopup, setShowAuditedObjectPopup] = useState<{ isShow: boolean, object: string }>({ isShow: false, object: "" });

  // CS-1586 - start 
  const [entityNumberLabel, setEntityNumberLabel] = useState<string>("Entity Number");

  useEffect(() => {
    const entityName = getValues("entityName");
    if (entityName) {
      switch (entityName.toUpperCase()) {
        case 'AGENCY':
          setEntityNumberLabel("Entity");
          break;
        case 'IVR':
          setEntityNumberLabel("Entity Number");
          break;
        case 'MANUFACTURER':
          setEntityNumberLabel("Entity");
          break;
        case 'ORDER':
          setEntityNumberLabel("Entity Number");
          break;
        case 'PRACTICE':
          setEntityNumberLabel("Entity");
          break;
        case 'PRODUCT':
          setEntityNumberLabel("Entity");
          break;
        case 'PRESCRIPTION':
          setEntityNumberLabel("Entity Number");
          break;
        case 'USER':
          setEntityNumberLabel("Entity");
          break;
        case 'BILLER':
          setEntityNumberLabel("Entity");
          break;
        default:
          setEntityNumberLabel("Entity Number");
          break;
      }
    }
  })
  // CS-1586 - end

  const searchAuditLogList = useCallback(
    (currentPage: number, obj?: SearchAuditLog) => {
      let data: SearchAuditLog = {
        fromDate:obj?.fromDate ,
        toDate:obj?.toDate,
        entityNumber: obj?.entityNumber,
        userName: obj?.userName,
        entityName: obj?.entityName,
        actionType:obj?.actionType,
        currentPage: currentPage,
        itemsPerPage: ITEMS_PER_PAGE,
      };
      setValue("currentPage", currentPage);
      setSearchCriteria(data)
      dispatch(fetchAuditLogList(data));
    },
    []
  );
  // CS-1405 - Start
  const [anchorElMenu, setAnchorElMenu] = useState<null | HTMLElement>(null);

  const canExportAuditLogList = useSelector((state: RootState) => selectIsValueFoundInToken("canAccessExportExcel", EXPORTABLE_ITEMS.AUDIT_LOG)(state));

  const handleMenuClick = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorElMenu(event.currentTarget);
  };

  const handleMenuClose = () => {
    setAnchorElMenu(null);
  };
  // CS-1405 - End

  //CS-518
  useEffect(() => {
    setShowCard(true)
    setValue("entityName", entityName);
    setValue("entityNumber", entityNumber);
    if(entityName&&entityNumber){
    searchAuditLogList(1, { ...getValues(), entityName: entityName, entityNumber: entityNumber })
    }
  }, [entityName,entityNumber])
  //
  const actionTypeOptions = useMemo(() => {
    let actions: CodeTable[] = [];
    if (allCodeTablesValuesMap) {
      actions = getCodeTableValuesByTableName(allCodeTablesValuesMap, ACTION_TYPE);
    }
    if (!actions || actions.length === 0) {
      dispatch(fetchAllCodeTablesValues());
    }
    let options:Option[] = [];
    actions?.map((actions) => {
      options.push({ label: actions.displayValue, value: actions.displayValue });
    });
    return options;
  }, [allCodeTablesValuesMap]);

  const entityNameOptions = useMemo(() => {
    let entity: CodeTable[] = [];
    if (allCodeTablesValuesMap) {
      entity = getCodeTableValuesByTableName(allCodeTablesValuesMap, ENTITY_TYPE);
    }
    if (!entity || entity.length === 0) {
      dispatch(fetchAllCodeTablesValues());
    }
    let options:Option[] = [];
    entity?.map((entity) => {
      options.push({ label: entity.displayValue, value: entity.displayValue });
    });
    return options;
  }, [allCodeTablesValuesMap]);

  //Unmount
  useEffect(() => {
    dispatch(resetAuditState());
  }, []);

  useEffect(() => {
    //1. When login another user pagination is remain in the history and can redirect that page
    if (location.state && location.state.auditLogListSeachCriteria) {
      console.log("%c Line:162 🥪 location", "color:#4fff4B", location);
      let searchCriteria : SearchAuditLog={
        entityName: 'Tesr',
        entityNumber: '',
        userName: '',
        actionType:[],
        fromDate: '',
        toDate: '',
        currentPage: 1,
        itemsPerPage:0
      }

      searchCriteria = location.state.auditLogListSeachCriteria;
      setSearchCriteria(searchCriteria)
      setValue("entityName",searchCriteria?.entityName)
      setValue("entityNumber",searchCriteria?.entityNumber)
      searchAuditLogList(searchCriteria?.currentPage ,searchCriteria)
    }
  }, [location.state]);

  const onSubmit = (data: SearchAuditLog) => {
    const data1 : SearchAuditLog ={
      entityName:data?.entityName,
      entityNumber:data?.entityNumber,
      userName:data?.userName,
      actionType:data?.actionType,
      fromDate:data?.fromDate,
      toDate:data?.toDate,
      currentPage:1,
      itemsPerPage:ITEMS_PER_PAGE
    }
    navigate("/auditlog", { state: { auditLogListSeachCriteria: data1 } });
  }

  const handlePagination = (pageNumber: number) => {
    const data = {
      ...searchCriteria,
      currentPage: pageNumber + 1
    }
    setSearchCriteria(data);

    //Below navigate line call to useEffect[location.state, productsByUserRoleCreateIVRSlice] and search
    navigate("/auditlog", { state: { auditLogListSeachCriteria: data } });
  }
  
  // CS-1405 - Start
  const handleExport = (exportType: string) => {
    const exportData = {
      ...searchCriteria,
      downloadState: true
    };

    ExportToExcel(
      exportData,
      columns,
      dispatch,
      EXPORTABLE_ITEMS.AUDIT_LOG
    );
  };
  // CS-1405 - End

 const handleDateRangeChange = (fromDate: DateObject | null, toDate: DateObject | null) => {
    const fromDateString = fromDate ? fromDate.format("MM/DD/YYYY") : null;
    const toDateString = toDate ? toDate.format("MM/DD/YYYY") : null;
    setValue("fromDate",fromDateString?fromDateString:"");
    setValue("toDate", toDateString?toDateString:"");
  };

  // Define columns and rows data
  const columns: Column[] = [
    { id: "entityName", label: "Audited Entity", minWidth: 100, align: "left" },
    { id: "entityNumber", label: "Entity #", minWidth: 100 , align: "left"},
    { id: "actionType", label: "Action Type", minWidth: 100 , align: "left"},
    { id: "userName", label: "User", minWidth: 100, align: "left" },
    { id: "auditMessage", label: "Audit Message", minWidth: 100 , align: "left"},
    { id: "auditedObject", label: "", minWidth: 10 , align: "left"},
    { id: "clientIP", label: "User IP", minWidth: 100 , align: "left"},
    { id: "auditedOn", label: "Audited ON", minWidth: 100, align: "left" }
  ];

  // CS-1235 
  const auditLogActionComponent = (row: any): JSX.Element => {
    return (
      <>
        {row.auditedObject && (
          <CustomIconButton
            color="success"
            size="small"
            variant="outlined"
            tooltipMessage="View Audit Object"
            tooltipPlacement="top"
            onClick={() => showAuditedObject(row.auditedObject)}
          >
            {/* <DataObjectOutlined fontSize="small" /> */}
            <AssignmentOutlined fontSize="small" />
          </CustomIconButton>
        )}
      </>
    );
  };
  const showAuditedObject = (data: any) => {
    setShowAuditedObjectPopup({ isShow: true, object: data });
  }
  //
  const formattedAuditLogRows = useMemo(() => {
    let formatRows: auditLogInfo[] = [];
    auditLogList?.content?.map((row) => {
      let formatRow: auditLogInfo = {
        ...row,
        entityName:row?.entityName,
        entityNumber:row?.entityNumber,
        actionType:row?.actionType,
        auditMessage:row?.action,
        auditedObject:row?.auditedObject !=null ? auditLogActionComponent(row) : null,
        clientIP:row?.clientIP,
        userName:row?.userName,
        auditedOn:row?.auditTimestamp,
      };
      formatRows.push(formatRow);
    });
    return formatRows;
  }, [auditLogList, getValues()]);

  return (
    <>
    {showAuditedObjectPopup.isShow &&
      <CustomJsonPopUp
        open={showAuditedObjectPopup.isShow}
        onClose={() => { setShowAuditedObjectPopup({ isShow: false, object: "" }) }}
        title="Audited Object"
        object={showAuditedObjectPopup.object}
        alwaysShowContent={true}
      />}
      <Box
        sx={{
          minHeight: "100vh",
          display: "flex",
          flexDirection: "column",
          px: { xs: 1, md: 3 },
          py: 2,
        }}
      >
        <Box sx={{ position: "relative", zIndex: 20, overflow: "visible" }}>
          <Collapse in={showCard} ref={filterPanelRef} onEntering={handleFilterPanelEntering} onEntered={handleFilterPanelEntered} onExited={handleFilterPanelExited}>
            <Card sx={filterPanelSx}>
              <form onSubmit={handleSubmit(onSubmit)}>
                <Grid container spacing={2} alignItems="flex-end">
                  <Grid item xs={12} md={3}>
                    <CustomDropdownField name="entityName" label="Audited Entity" options={entityNameOptions} control={control} size="small"
                      onChangeSelect={(data) => {
                        if (data) {
                          setSearchCriteria(prevState => ({
                            ...prevState,
                            entityName: data.value,
                            entityNumber:null
                          }));
                          setValue('entityNumber','')
                          setValue('actionType',[])
                        } else {
                          setSearchCriteria(prevState => ({
                            ...prevState,
                            entityName: null,
                            entityNumber:null
                          }));
                          setValue('entityNumber', '')
                          setValue('actionType',[])
                        }
                      }} />
                  </Grid>
                  <Grid item xs={12} md={3}>
                    <CustomTextField controllerName="entityNumber" label={entityNumberLabel} control={control} size="small"
                    onChange={(data) => {
                        if (data?.target.value) {
                          setSearchCriteria(prevState => ({
                            ...prevState,
                            entityNumber: data?.target.value
                          }));
                        } 
                        else {
                          setSearchCriteria(prevState => ({
                            ...prevState,
                            entityNumber: null
                          }));
                        }
                      }}/>
                  </Grid>
                  <Grid item xs={12} md={3}>
                    <CustomMultipleDropDown name="actionType" label="Action Type" options={actionTypeOptions} control={control} size="small" disabled={!searchCriteria?.entityName&&!searchCriteria?.entityNumber} />
                  </Grid>
                  <Grid item xs={12} md={3}>
                    <CustomTextField controllerName="userName" label="User Name" control={control} size="small"
                    onChange={(data) => {
                        if (data?.target.value) {
                          setSearchCriteria(prevState => ({
                            ...prevState,
                            userName: data?.target.value
                          }));
                        } 
                        else {
                          setSearchCriteria(prevState => ({
                            ...prevState,
                            userName: null
                          }));
                        }
                      }}/>
                  </Grid>
                  <Grid item xs={12} md={3}>
                    <CustomDateRange
                      label="Update Date Range"
                      control={control}
                      controllerName="dateRange"
                      dateRangeMonths={!entityNumber ? 6 : 0}
                      onChange={handleDateRangeChange}
                    />
                  </Grid>
                  <Grid item xs={12} md={2}>
                    <Button
                      fullWidth
                      size="large"
                      startIcon={<SearchOutlined />}
                      disabled={!searchCriteria?.userName&&!searchCriteria?.entityName&&!searchCriteria?.entityNumber}
                      type="submit"
                      removeUpperCase
                      sx={{ ...primaryActionBtnSx, height: "42px", py: 0 }}
                    >
                      Search
                    </Button>
                  </Grid>
                </Grid>
              </form>
            </Card>
          </Collapse>
        </Box>

        <Box mt={2} sx={{ position: "relative", zIndex: 1 }}>
          <Card sx={{ ...listCardSx, p: { xs: "14px !important", md: "18px !important" } }}>
            <Grid container justifyContent="space-between" alignItems="center" spacing={1.2}>
              <Grid item xs={12} md>
                <Typography variant="h3" fontWeight={800} color="text.primary" data-testid="header">
                  Audit Log
                </Typography>
              </Grid>
              <Grid item xs={12} md="auto">
                <Button
                  variant="outlined"
                  color="info"
                  onClick={toggleFilterPanel}
                  startIcon={<SearchOutlined />}
                  fullWidth
                  removeUpperCase
                  sx={outlineBtnSx}
                >
                  {showCard ? "Hide Filters" : "Show Filters"}
                </Button>
              </Grid>
              {/* CS-1405 - Start */}
              {canExportAuditLogList &&
                <Grid item sm="auto">
                  <Box>
                    {!isMobile &&
                      (<Button
                        id="customized-button"
                        aria-controls={Boolean(anchorElMenu) ? "customized-menu" : undefined}
                        aria-haspopup="true"
                        aria-expanded={Boolean(anchorElMenu) ? "true" : undefined}
                        variant="outlined"
                        disableElevation
                        onClick={handleMenuClick}
                        startIcon={<ExitToApp />}
                        removeUpperCase
                        sx={exportBtnSx}
                        endIcon={<Box
                          component="span"
                          sx={{
                            display: "flex",
                            transition: "transform 0.3s ease",
                            transform: anchorElMenu ? "rotate(180deg)" : "rotate(0deg)",
                            padding: "2px"
                          }}
                        >
                          <KeyboardArrowDown />
                        </Box>}
                      >
                        Export
                      </Button>)
                    }
                    <StyledMenu
                      id="customized-menu"
                      MenuListProps={{
                        "aria-labelledby": "customized-button",
                      }}
                      anchorEl={anchorElMenu}
                      open={Boolean(anchorElMenu)}
                      onClose={handleMenuClose}
                      slotProps={{
                        paper: {
                          sx: {
                            borderRadius: "12px",
                            border: `1px solid ${listBorderColor}`,
                            backgroundColor: listPanelBg,
                            boxShadow: isDark ? "0 16px 40px rgba(0,0,0,0.45)" : "0 16px 40px rgba(15,23,42,0.12)",
                            mt: 1,
                            minWidth: 200,
                            "& .MuiMenuItem-root": {
                              borderRadius: "8px",
                              mx: 0.75,
                              my: 0.35,
                              "&:hover": { backgroundColor: listHoverBg },
                            },
                          },
                        },
                      }}
                    >
                      <MenuItem onClick={() => handleExport("searchList")}>
                        <ListItemIcon>
                          <CloudDownload />
                        </ListItemIcon>
                        <Typography variant="inherit" noWrap>
                          Export List
                        </Typography>
                      </MenuItem>
                    </StyledMenu>
                  </Box>
                </Grid>
              }
              {/* CS-1405 - End */}
            </Grid>
            {/* CS-1405 - Start */}
            {canExportAuditLogList &&
              <Box mt={1}>
                {isMobile && 
                  (<Button
                    color="success"
                    variant="text"
                    onClick={(event) => handleMenuClick(event)}
                    startIcon={<DownloadOutlined fontSize="small" />}
                    removeUpperCase
                    sx={exportBtnSx}
                  >
                    Export
                  </Button>)
                }
              </Box>
            }
            {/* CS-1405 - End */}
            <Table
              columns={columns}
              rows={formattedAuditLogRows||[]}
              pageNumber={handlePagination}
              totalPages={auditLogList?.totalPages}
              totalElementText={"Records: " + (auditLogList?.totalElements ? auditLogList?.totalElements : "0")}
              emptyText="No Log(s) Found"
              currentPageNumber={getValues("currentPage") ? getValues("currentPage") - 1 : 1}
              showMenu={true}
              sx={auditTableSx}
            />
          </Card>
        </Box>
      </Box>
    </>
  )
}

export default AuditLogList;